import type { HttpPort, HttpRequestOptions, Json } from "../contracts";
import { HttpError, PermissionError, ServerError, UnauthorizedError } from "./errors";

// Fetch-based HttpPort implementation. Pure TypeScript — it depends on the Fetch
// API (web-standard, injectable for tests), never on React or a concrete auth
// service. Token access + refresh are supplied as callbacks, so the adapter wires
// it to whatever session store it uses. This is the "give me a real API" seam.
export interface HttpClientOptions {
    baseUrl?: string;
    getAccessToken?: () => string | null;
    refreshAccessToken?: () => Promise<string | null>;
    onUnauthorized?: () => void;
    emit?: (name: string, payload: Json) => void;
    fetchFn?: typeof fetch;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export class HttpClient implements HttpPort {
    private readonly baseUrl: string;
    private readonly getAccessToken: () => string | null;
    private readonly refreshAccessToken?: () => Promise<string | null>;
    private readonly onUnauthorized?: () => void;
    private readonly emit?: (name: string, payload: Json) => void;
    private readonly fetchFn: typeof fetch;
    // Single-flight refresh: concurrent 401s share one refresh round-trip.
    private refreshing: Promise<string | null> | null = null;

    constructor(opts: HttpClientOptions = {}) {
        this.baseUrl = opts.baseUrl ?? "";
        this.getAccessToken = opts.getAccessToken ?? (() => null);
        this.refreshAccessToken = opts.refreshAccessToken;
        this.onUnauthorized = opts.onUnauthorized;
        this.emit = opts.emit;
        this.fetchFn = opts.fetchFn ?? globalThis.fetch.bind(globalThis);
    }

    get<T = unknown>(path: string, opts?: HttpRequestOptions): Promise<T> {
        return this.request<T>("GET", path, undefined, opts);
    }

    post<T = unknown>(path: string, body?: Json, opts?: HttpRequestOptions): Promise<T> {
        return this.request<T>("POST", path, body, opts);
    }

    put<T = unknown>(path: string, body?: Json, opts?: HttpRequestOptions): Promise<T> {
        return this.request<T>("PUT", path, body, opts);
    }

    patch<T = unknown>(path: string, body?: Json, opts?: HttpRequestOptions): Promise<T> {
        return this.request<T>("PATCH", path, body, opts);
    }

    delete<T = unknown>(path: string, opts?: HttpRequestOptions): Promise<T> {
        return this.request<T>("DELETE", path, undefined, opts);
    }

    async getBlob(path: string, opts?: HttpRequestOptions): Promise<Blob> {
        // Bypasses the JSON-parsing `request<T>` path — the body is
        // raw bytes (image preview, document download, etc.) and
        // `Response.blob()` is the right consumer. Auth header +
        // 401 refresh still flow through `buildHeaders` and the
        // single-flight refresh.
        const headers = this.buildHeaders(opts?.headers);
        // The "Content-Type: application/json" default on the request
        // body doesn't apply to GETs (no body), but the request
        // wrapper sets it anyway. Strip it so the server doesn't see a
        // misleading content-type on a body-less request.
        delete headers["Content-Type"];
        const response = await this.fetchFn(`${this.baseUrl}${path}`, {
            method: "GET",
            headers,
            signal: opts?.signal,
        });
        if (response.status === 401 && this.refreshAccessToken) {
            const token = await this.refreshOnce();
            if (token) return this.getBlob(path, opts);
        }
        if (response.status === 401) {
            this.onUnauthorized?.();
            throw new UnauthorizedError();
        }
        if (!response.ok) {
            throw this.toError(response.status, await this.safeJson(response));
        }
        return response.blob();
    }

    async postBytes<T = unknown>(
        path: string,
        body: ArrayBuffer | Uint8Array,
        mimetype: string,
        opts?: HttpRequestOptions,
    ): Promise<T> {
        // Raw-bytes upload — used for image uploads and any other
        // path where the backend reads the binary directly from the
        // request body. Content-Type carries the mimetype so the
        // server knows what it's receiving. Response is JSON (the
        // server returns metadata like the created attachment uuid).
        const headers = this.buildHeaders(opts?.headers);
        headers["Content-Type"] = mimetype || "application/octet-stream";
        // Cast ArrayBufferLike → BodyInit (TypeScript's fetch types
        // accept ArrayBuffer + Uint8Array; the runtime cast is safe).
        const response = await this.fetchFn(`${this.baseUrl}${path}`, {
            method: "POST",
            headers,
            body: body as BodyInit,
            signal: opts?.signal,
        });
        if (response.status === 401 && this.refreshAccessToken) {
            const token = await this.refreshOnce();
            if (token) return this.postBytes<T>(path, body, mimetype, opts);
        }
        if (response.status === 401) {
            this.onUnauthorized?.();
            throw new UnauthorizedError();
        }
        if (!response.ok) {
            throw this.toError(response.status, await this.safeJson(response));
        }
        if (response.status === 204) return undefined as T;
        return (await this.safeJson(response)) as T;
    }

    private buildHeaders(extra?: Record<string, string>): Record<string, string> {
        const headers: Record<string, string> = { "Content-Type": "application/json", ...extra };
        const token = this.getAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
        return headers;
    }

    private async request<T>(
        method: Method,
        path: string,
        body: Json | undefined,
        opts?: HttpRequestOptions,
        isRetry = false,
    ): Promise<T> {
        const response = await this.fetchFn(`${this.baseUrl}${path}`, {
            method,
            headers: this.buildHeaders(opts?.headers),
            body: body === undefined ? undefined : JSON.stringify(body),
            signal: opts?.signal,
        });

        if (response.status === 401 && !isRetry && this.refreshAccessToken) {
            const token = await this.refreshOnce();
            if (token) return this.request<T>(method, path, body, opts, true);
        }
        if (response.status === 401) {
            this.onUnauthorized?.();
            throw new UnauthorizedError();
        }

        if (!response.ok) {
            throw this.toError(response.status, await this.safeJson(response));
        }
        if (response.status === 204) {
            return undefined as T;
        }
        return (await this.safeJson(response)) as T;
    }

    private refreshOnce(): Promise<string | null> {
        if (!this.refreshAccessToken) return Promise.resolve(null);
        if (!this.refreshing) {
            this.refreshing = this.refreshAccessToken().finally(() => {
                this.refreshing = null;
            });
        }
        return this.refreshing;
    }

    private async safeJson(response: Response): Promise<unknown> {
        const text = await response.text();
        if (!text) return undefined;
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    }

    private toError(status: number, payload: unknown): HttpError {
        const message = this.messageOf(payload, `HTTP ${status}`);
        if (status === 403) {
            const p = (payload ?? {}) as { resource?: string; action?: string };
            this.emit?.("permission.denied", { resource: p.resource ?? null, action: p.action ?? null });
            return new PermissionError(message, p.resource, p.action, payload);
        }
        if (status >= 500) {
            const p = (payload ?? {}) as { traceback?: string };
            this.emit?.("server.error", { message, traceback: p.traceback ?? null });
            return new ServerError(message, p.traceback, payload);
        }
        return new HttpError(status, message, payload);
    }

    private messageOf(payload: unknown, fallback: string): string {
        if (payload && typeof payload === "object") {
            const p = payload as Record<string, unknown>;
            for (const key of ["message", "detail", "error"]) {
                if (typeof p[key] === "string") return p[key] as string;
            }
        }
        if (typeof payload === "string" && payload.length > 0) return payload;
        return fallback;
    }
}
